import React from 'react';
import { Box, Typography } from '@mui/material';
import { CombinedText } from '../insightsTypes';
import HorizontalBarChart, { type HorizontalBarItem } from '@/components/common/charts/HorizontalBarChart';
import OverviewShell from './slices/OverviewShell';
import MetricHeading from './slices/MetricHeading';
import NoInsightsOverview from './slices/NoInsightsOverview';
import { COMBINED_BAR_COLOR, COMBINED_TEXT_COLOR, NEUTRAL_TRACK, OTHER_THEME_BAR_COLOR } from '../insightsTheme';

// Combined overview for free-form questions: total responses + the most discussed themes.
const FreeformCategoryOverview: React.FC<{ combined: CombinedText }> = ({ combined }) => {
    if (combined.overall.responseCount === 0) {
        return <NoInsightsOverview />;
    }

    const { topThemes, otherThemes, totalMentions, topThemesPercent } = combined.overall;

    // "Other" is always shown last because it represents all unranked themes combined.
    const rowsToDraw = otherThemes ? [...topThemes, otherThemes] : topThemes;

    const themeRows: HorizontalBarItem[] = rowsToDraw.map((theme) => ({
        label: theme.label,
        percentage: theme.percentOfAllMentions,
        displayValue: `${theme.roundedPercent}%`,
        tooltip: `${theme.mentionCount} of ${totalMentions} mentions`,
        // Keep "Other" visually separate from ranked themes.
        ...(theme === otherThemes ? { barColor: OTHER_THEME_BAR_COLOR } : {}),
    }));

    return (
        <OverviewShell>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '230px 1fr' }, gap: 4, alignItems: 'center' }}>
                <Box>
                    <MetricHeading>Total responses</MetricHeading>
                    <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1, color: 'text.primary', mt: 1 }}>
                        {combined.overall.responseCount.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        AI-clustered into themes across all free-text answers
                    </Typography>
                </Box>
                {themeRows && themeRows.length > 0 && (
                    <Box sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 } }}>
                        {/* Combined themes show discussion volume across all free-text questions  */}
                        <MetricHeading>Top themes</MetricHeading>
                        <Box sx={{ mt: 1.5 }}>
                            {/* Combined view: one calm blue, no good/bad colour coding. */}
                            <HorizontalBarChart
                                rows={themeRows}
                                barColor={COMBINED_BAR_COLOR}
                                trackColor={NEUTRAL_TRACK}
                                valueColor={COMBINED_TEXT_COLOR}
                            />
                        </Box>
                        {/* Shows how much of the total is covered by named themes vs. "Other". */}
                        {otherThemes !== null && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                Other groups themes mentioned too rarely to list on their own. The {topThemes.length} above cover{' '}
                                {topThemesPercent}% of everything mentioned.
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </OverviewShell>
    );
};

export default FreeformCategoryOverview;
